import { Outlet, createRootRouteWithContext } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button, EmptyState } from '@/ui'
import { IconError } from '@/icons'
import { log } from '@/lib/log'

export interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
})

function RootLayout() {
  const { t } = useTranslation()

  return (
    <>
      {/* First tab stop on every page, so a keyboard user can skip the sidebar. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-control focus:bg-primary focus:px-3 focus:py-2 focus:text-text-on-primary"
      >
        {t('app.skipToContent')}
      </a>
      <Outlet />
    </>
  )
}

function NotFound() {
  const { t } = useTranslation()

  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <EmptyState
        title={t('state.notFoundTitle')}
        description={t('state.notFoundHint')}
        action={
          <Button variant="secondary" onClick={() => { window.location.assign('/') }}>
            {t('state.goHome')}
          </Button>
        }
      />
    </div>
  )
}

function RouteError({ error }: { error: Error }) {
  const { t } = useTranslation()
  log.exception('A route threw', error)

  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <EmptyState
        icon={<IconError size={22} className="text-danger" />}
        title={t('state.errorTitle')}
        description={error.message}
        action={
          <Button variant="secondary" onClick={() => { window.location.reload() }}>
            {t('common.retry')}
          </Button>
        }
      />
    </div>
  )
}
